import {
  Alert,
  Box,
  Button,
  Checkbox,
  Grid,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useFormik } from 'formik';
import * as yup from 'yup';

import { store } from '../../store';
import { register } from '../../features/login/loginSlice';
import { useState } from 'react';

import SeguridadModal from '../SeguridadModal';

import sanMiguelLogo from '../../assets/login-logo.png';
import UncheckedIcon from '../../assets/icons/uncheckbox-icon';
import CheckboxIcon from '../../assets/icons/checkbox-icon';

interface RegisterProps {
  token: string;
  creationUrl: string;
}

export default function Register(props: RegisterProps) {
  const { token, creationUrl } = props;

  const navigate = useNavigate();
  const theme = useTheme();

  const [openModal, setOpenModal] = useState(false);

  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(false);

  const validationSchema = yup.object({
    name: yup.string().required('El nombre y apellido son obligatorios'),
    email: yup
      .string()
      .email('Ingrese un correo electrónico válido')
      .required('El correo electrónico es obligatorio'),
    phone: yup
      .string()
      .max(20, 'El teléfono debe tener como máximo 20 dígitos')
      .matches(/^\d+$/, 'El teléfono debe ser un número')
      .required('El teléfono es obligatorio'),
    terms: yup
      .boolean()
      .oneOf([true], 'Aceptar el Reglamento de Protección y Seguridad de los Datos es requerido')
      .required('Aceptar el Reglamento de Protección y Seguridad de los Datos es requerido'),
  });

  const formik = useFormik({
    initialValues: {
      name: '',
      email: '',
      phone: '',
      terms: false,
    },
    validationSchema: validationSchema,
    onSubmit: values => {
      // Reseteamos los estados
      setError(false);
      setSuccess(false);

      // Hacemos el dispatch del registro
      store
        .dispatch(
          register({
            nombre: values.name,
            email: values.email,
            telefono: values.phone,
            token: token,
            url: creationUrl,
          })
        )
        .unwrap()
        .then(() => {
          // Si el usuario ya completó el registro lo redirigimos al dashboard
          navigate('/');
          return;
        })
        .catch(err => {
          const status = store.getState();
          // Si el error es de verificación de mail consideramos el registro exitoso
          if (status.login.errorCode === 'MUST_VERIFY_EMAIL') {
            setSuccess(true);
            return;
          }

          // En otro caso hacemos log y mostramos mensaje de error
          console.error(err);
          setError(true);
        });
    },
  });

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
    >
      <Grid container>
        <Grid item xs={2} md={4} />
        <Grid item xs={8} md={4}>
          <Box
            component="form"
            action="#"
            onSubmit={formik.handleSubmit}
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            sx={{
              border: `1px solid ${theme.palette.grey[300]}`,
              padding: '2rem',
              margin: '2rem',
            }}
            noValidate
            autoComplete="off"
          >
            <img
              src={sanMiguelLogo}
              style={{ maxWidth: '100%', height: 'auto', marginBottom: '2rem' }}
            />
            {success ? (
              <Typography
                variant="body2"
                mb="1.5rem"
                sx={{
                  fontSize: '1.125rem',
                  lineHeight: '1.75rem',
                  textAlign: 'center',
                }}
              >
                Se envió un mensaje de correo electrónico a la casilla indicada
                para su verificación.
                <br />
                Haga click en enlace que aparece en el mismo para completar el
                proceso.
              </Typography>
            ) : (
              <>
                <Typography
                  variant="body2"
                  mb="1.5rem"
                  sx={{
                    fontSize: '1.125rem',
                    lineHeight: '1.75rem',
                    textAlign: 'center',
                  }}
                >
                  Como es la primera vez que ingresa en el sistema necesitamos
                  algunos datos adicionales para poder completar el proceso de
                  registro.
                </Typography>
                <Typography
                  variant="body2"
                  mb="1.5rem"
                  sx={{
                    fontSize: '1.125rem',
                    lineHeight: '1.75rem',
                    textAlign: 'center',
                  }}
                >
                  Por favor ingrese la información solicitada a continuacion:
                </Typography>
                <TextField
                  fullWidth
                  variant="outlined"
                  label="Nombre y Apellido"
                  id="name"
                  name="name"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.name}
                  error={formik.touched.name && Boolean(formik.errors.name)}
                  helperText={formik.touched.name && formik.errors.name}
                  disabled={success}
                  sx={{ marginBottom: '1rem' }}
                />
                <TextField
                  fullWidth
                  variant="outlined"
                  label="Correo Electrónico"
                  id="email"
                  name="email"
                  type="email"
                  inputProps={{ style: { textTransform: 'lowercase' } }}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.email}
                  error={formik.touched.email && Boolean(formik.errors.email)}
                  helperText={formik.touched.email && formik.errors.email}
                  disabled={success}
                  sx={{ marginBottom: '1rem' }}
                />
                <TextField
                  fullWidth
                  variant="outlined"
                  label="Teléfono"
                  id="phone"
                  name="phone"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.phone}
                  error={formik.touched.phone && Boolean(formik.errors.phone)}
                  helperText={formik.touched.phone && formik.errors.phone}
                  disabled={success}
                />
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    marginBottom:
                      formik.errors.terms && formik.touched.terms
                        ? '0rem'
                        : '1rem',
                  }}
                >
                  <Checkbox
                    id="terms"
                    name="terms"
                    onClick={formik.handleChange}
                    value={formik.values.terms}
                    icon={ <UncheckedIcon /> }
                    checkedIcon={ <CheckboxIcon />}
                  />

                  <Typography>
                    Acepto el{' '}
                    <span
                      onClick={() => setOpenModal(!openModal)}
                      style={{
                        color: theme.palette.primary.main,
                        cursor: 'pointer',
                      }}
                    >
                      Reglamento de Protección y Seguridad de los Datos.
                    </span>
                  </Typography>
                </Box>
                { formik.errors.terms && formik.touched.terms && (
                  <Typography
                    sx={{
                      fontSize: '.75rem',
                      marginX: '1rem',
                      marginY: '.5rem',
                      color: '#D64548',
                    }}
                  >
                    { formik.errors.terms}
                  </Typography>
                )}
                <Button
                  variant="outlined"
                  sx={{ mt: '1rem' }}
                  type="submit"
                  disabled={success}
                >
                  Completar Registro
                </Button>
                <SeguridadModal
                  openModal={openModal}
                  setOpenModal={setOpenModal}
                />
              </>
            )}
            {error ? (
              <Alert severity="warning" sx={{ marginTop: '1rem' }}>
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: '1.125rem',
                    lineHeight: '1.5rem',
                    textAlign: 'center',
                  }}
                >
                  Se produjo un error al intentar registrar el usuario. Por
                  favor, intente nuevamente.
                </Typography>
              </Alert>
            ) : null}
          </Box>
        </Grid>
        <Grid item xs={2} md={4} />
      </Grid>
    </Box>
  );
}
